import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Image as ImageIcon } from "lucide-react";
import { ScreenshotDialog } from "@/components/ScreenshotDialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Panel, PageHead, StatusBadge, Empty } from "@/components/common";
import { RejectDialog } from "@/components/RejectDialog";
import { useApi, errMsg, inr, fmtDate } from "@/lib/api";

export const ReviewPage = ({ base, title, kind, approveMsg }) => {
  const api = useApi();
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [viewing, setViewing] = useState(null);
  const load = useCallback(() => api("get", `${base}${status === "all" ? "" : `?status=${status}`}`).then(setRows), [api, status, base]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body, msg) => {
    try { await api("post", path, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={`admin-${kind}-page`}>
      <PageHead label="Admin" title={title} />
      <Panel>
        <Tabs value={status} onValueChange={setStatus} className="mb-6">
          <TabsList>{["pending", "approved", "rejected", "all"].map((s) => <TabsTrigger key={s} value={s} data-testid={`payments-tab-${s}`} className="capitalize">{s}</TabsTrigger>)}</TabsList>
        </Tabs>
        {!rows ? <p className="text-sm text-slate-500">Loading…</p> : rows.length === 0 ? <Empty testId="payments-empty" text="Nothing here." /> : (
          <Table data-testid="payments-table">
            <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>UTR</TableHead><TableHead>Amount</TableHead><TableHead>Submitted</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`${kind}-row-${p.utr}`}>
                  <TableCell><p className="text-white">{p.name}</p><p className="text-xs text-slate-500">{p.email}</p></TableCell>
                  <TableCell className="font-mono text-emerald-400">{p.utr}
                    {p.has_screenshot && <button className="ml-2 inline-flex items-center gap-1 rounded border border-[#1F293D] px-1.5 py-0.5 font-sans text-[11px] text-slate-300 hover:border-emerald-500/50" data-testid={`view-screenshot-btn-${p.utr}`} onClick={() => setViewing(p)}><ImageIcon className="h-3 w-3" />Proof</button>}
                  </TableCell>
                  <TableCell className="font-mono">{inr(p.amount)}
                    {p.product_name && <p className="mt-1 font-sans text-xs text-white" data-testid={`payment-product-${p.utr}`}>{p.product_name}</p>}
                    {p.address && <p className="mt-0.5 max-w-[220px] whitespace-pre-line font-sans text-[11px] text-slate-500">{p.address}</p>}
                  </TableCell>
                  <TableCell className="text-xs">{fmtDate(p.created_at)}</TableCell>
                  <TableCell><StatusBadge status={p.status} />{p.reason && <p className="mt-1 text-xs text-red-400">{p.reason}</p>}</TableCell>
                  <TableCell className="text-right">
                    {p.status === "pending" && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" data-testid={`approve-${kind}-btn-${p.utr}`} onClick={() => act(`${base}/${p.id}/approve`, {}, approveMsg)}><Check className="mr-1 h-4 w-4" />Approve</Button>
                        <Button size="sm" variant="outline" data-testid={`reject-${kind}-btn-${p.utr}`} onClick={() => setRejecting(p)}><X className="mr-1 h-4 w-4" />Reject</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <RejectDialog open={!!rejecting} title={`Reject UTR ${rejecting?.utr || ""}`} onClose={() => setRejecting(null)}
        onConfirm={(reason) => { act(`${base}/${rejecting.id}/reject`, { reason }, "Rejected"); setRejecting(null); }} />
      <ScreenshotDialog payment={viewing} base={base} onClose={() => setViewing(null)} />
    </div>
  );
};

export default function Payments() {
  return <ReviewPage base="/admin/payments" title="UTR approvals" kind="utr" approveMsg="Member activated" />;
}
